/**
 * Conversions from the workspace's own website — slice 63, on Outcomes.
 *
 * With click tracking on, every link in a campaign email passes through Wend,
 * and the destination receives `?wend_click=…`. A website that keeps that value
 * can tell Wend when the person converts, and the conversion lands as Matched
 * with no spreadsheet. This card is where the website's signing key is made, and
 * where whoever wires the website finds the three values they need.
 *
 * ## THE KEY IS SHOWN ONCE
 *
 * The answer that makes a key is the only place it ever appears; the server
 * stores it sealed and has no route that reads it back. So the key arrives in a
 * copy field that takes focus — a screen reader announces it, and it is the
 * thing on the page that matters at that moment — with one label saying this is
 * the only time. Once there is a key, the card offers only "Replace the key",
 * behind a confirmation that the old one stops working at once.
 *
 * ## WHAT A DEVELOPER NEEDS, AND NOT MORE
 *
 * The workspace id, the endpoint and each conversion's id are copyable rows;
 * how to sign is a literal example behind `MoreDetail`, saying the two things a
 * competent developer would otherwise get wrong — sign the body byte for byte,
 * and use the key's text as given, not base64-decoded — and that no other field
 * is accepted.
 *
 * ## THE WEBSITE IS DRAWN AS A SOURCE — SLICE 66
 *
 * The Blueprint draws every place data comes from as a `SourceCard`: a glyph,
 * a mono kicker saying what kind of source it is, its name, and its state in
 * the corner. The website is one more such source of conversions, so it gets
 * the same card, and the key's state — "none yet", "made 6 Oct", "made just
 * now" — moved from a line of prose into the card's status badge. The badge
 * is this component's own reading of `status` and `created`, the same three
 * cases the prose line had, word for word after "Key".
 *
 * It is NEUTRAL in every case, and the card is never emphasised. In the
 * product's SourceCard idiom (the Integrations rail wears the same skin) a
 * brand-sweep tile and a green badge mean "this source is connected and
 * working". The first slice 66 build turned both on as soon as a key existed —
 * but `PostbackKeyStatus` holds only `exists` and `createdAt`. Nothing records
 * that the website has sent a postback, or is wired at all, so a key made for
 * a site nobody wired showed as a green, connected "Your website" (review).
 * A made key is set-up progress, said in words. What would earn `good` and the
 * emphasis is a received-postback signal ("last postback 6 Oct"), which the
 * API does not have; add that first. "None yet" stays neutral, not a warning —
 * many workspaces will never wire a website, and an amber badge would nag them.
 */
import { useEffect, useRef, useState } from 'react';

import {
  CLICK_ID_PARAM,
  POSTBACK_SIGNATURE_HEADER,
  type OutcomeType,
  type PostbackKeyCreated,
  type PostbackKeyStatus,
} from '@wend/shared';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, SourceCard } from '@wend/ui';
import { Globe } from 'lucide-react';

import { formatDate } from '../../lib/format';
import { CopyableValue } from '../integrations/CopyableValue';
import { MoreDetail } from '../templates/MoreDetail';

export interface PostbackCardProps {
  readonly status: PostbackKeyStatus;
  /** The postback endpoint, absolute. */
  readonly endpoint: string;
  /** The workspace's conversions, whose ids a postback names. */
  readonly types: readonly OutcomeType[];
  readonly onCreate: () => Promise<PostbackKeyCreated>;
}

export function PostbackCard({ status, endpoint, types, onCreate }: PostbackCardProps) {
  const [created, setCreated] = useState<PostbackKeyCreated | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const keyRegion = useRef<HTMLDivElement | null>(null);

  // The button that made the key unmounts; focus goes to the key, not to <body>.
  useEffect(() => {
    if (created !== null) keyRegion.current?.focus();
  }, [created]);

  async function make() {
    setPending(true);
    setFailure(null);
    try {
      setCreated(await onCreate());
      setConfirming(false);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The key could not be made.');
    } finally {
      setPending(false);
    }
  }

  const hasKey = status.exists || created !== null;
  const keyState =
    created !== null
      ? 'made just now'
      : status.exists && status.createdAt !== null
        ? `made ${formatDate(status.createdAt)}`
        : 'none yet';
  const example = JSON.stringify({
    workspace: status.workspaceId,
    clickId: '<the wend_click value>',
    outcomeTypeId: types[0]?.id ?? '<a conversion id>',
    value: 2500,
    occurredOn: '2026-10-06',
  });

  return (
    <Card data-role="postback-card">
      <CardHeader>
        <CardTitle>Conversions from your website</CardTitle>
        <CardDescription>
          When someone clicks a link in your email and then converts on your website, the website can tell Wend, and
          it counts as a matched conversion for that person. It needs click tracking switched on in Integrations.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <SourceCard
          className="max-w-sm"
          icon={<Globe />}
          kind="Website · signed postback"
          name="Your website"
          via="Tells Wend who converted after clicking a link"
          status={
            <Badge tone="neutral" data-role="postback-key-state">
              Key · {keyState}
            </Badge>
          }
        />
        <div className="flex max-w-2xl flex-col gap-2" data-role="postback-values">
          <CopyableValue label="Workspace id" value={status.workspaceId} />
          <CopyableValue label="Endpoint" value={endpoint} />
          {types.map((type) => (
            <CopyableValue key={type.id} label={type.name} value={type.id} />
          ))}
        </div>


        {created !== null && (
          <div className="flex max-w-2xl flex-col gap-1" data-role="postback-new-key" ref={keyRegion} tabIndex={-1} aria-live="polite">
            <p className="text-[0.82rem] text-muted">Copy it now. Wend cannot show it again.</p>
            <CopyableValue label="Postback key" value={created.key} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {!hasKey && (
            <Button variant="secondary" onClick={() => void make()} disabled={pending}>
              Make a key
            </Button>
          )}
          {hasKey && !confirming && (
            <Button variant="secondary" onClick={() => setConfirming(true)} disabled={pending}>
              Replace the key
            </Button>
          )}
          {hasKey && confirming && (
            <>
              <span className="text-[0.85rem] text-text">The current key stops working at once.</span>
              <Button variant="secondary" onClick={() => void make()} disabled={pending}>
                Replace it
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(false)} disabled={pending}>
                Keep it
              </Button>
            </>
          )}
        </div>
        {failure !== null && (
          <p className="text-[0.85rem] text-bad" role="alert">
            {failure}
          </p>
        )}

        <MoreDetail summary="For whoever wires the website">
          <p>
            Each link in a campaign email arrives at your page with <code>?{CLICK_ID_PARAM}=…</code> added. Keep that
            value, and when the person converts, send:
          </p>
          <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-base border border-line p-3 font-mono text-[0.76rem]">
            {`POST ${endpoint}\n${POSTBACK_SIGNATURE_HEADER}: sha256=<hex>\ncontent-type: application/json\n\n${example}`}
          </pre>
          <p>
            The signature is the hex HMAC-SHA-256 of the request body, byte for byte as sent, keyed with the key text
            exactly as shown — do not decode it. <code>value</code> (rupees) and <code>occurredOn</code> (YYYY-MM-DD,
            not in the future) are optional; no other fields are accepted. Wend records one conversion per person, per
            kind, per day; a repeat is answered <code>recorded: false</code>.
          </p>
        </MoreDetail>
      </CardContent>
    </Card>
  );
}
